import { gql } from "@apollo/client";
import { InitializeBillingDialog } from "@src/features/app/top-nav/InitializeBillingDialog";
import WipeTrialOrgDialog from "./WipeTrialOrgDialog";
import SetActionPermissions, {
  SetUserType,
} from "@src/features/app/top-nav/SetActionPermissions";
import CreateOrgDialog from "@src/features/app/dialogs/orgManagement/CreateOrgDialog";
import UpdateOrgDialog from "@src/features/app/dialogs/orgManagement/UpdateOrgDialog";
import { useOrgForUpdateQuery } from "@src/features/app/dialogs/orgManagement/UpdateOrgDialog.generated";
import Text from "@src/lib/ui/Text";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import useUser from "@src/features/app/hooks/useUser";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import React, { useState } from "react";
import { useResetDemoOrgMutation } from "./AdminTools.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useToast } from "@src/lib/hooks/useToast";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { DropdownMenuItem } from "@src/lib/ui/DropdownMenu";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import SetEmployeeFeatureFlags from "@src/features/app/top-nav/SetEmployeeFeatureFlags";
import GlobeIcon from "@src/lib/icons/18px/globe";
import SetOrgFeatureFlags from "./SetOrgFeatureFlags";
import OptInToOctoberRelease from "./OptInToOctoberRelease";
import ImpersonateUser from "./ImpersonateUser";
import { useCanImpersonate } from "@src/features/app/hooks/useImpersonation";
import { Tabs, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";

/**
 * Returns a pair { menuItem, dialog } for staff users to access the admin
 * tools. Render `menuItem` inside a `DropdownMenu`, and `dialog` as a sibling
 * of the menu so it survives the menu closing.
 */
export const useAdminToolsMenu = (): {
  menuItem: React.ReactNode;
  dialog: React.ReactNode;
  openAdminTools: () => void;
} => {
  const { isStaff } = useIsStaff();
  const [open, setOpen] = useState(false);
  const [wipeTrialOrg, setWipeTrialOrg] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const { user } = useUser();
  const { userActionPermissions } = useActionPermissions();
  const canWipeTrialOrg =
    isStaff && userActionPermissions?.changeOrgSettings && !!user?.org;
  const activeWipeOrg =
    canWipeTrialOrg && wipeTrialOrg?.id === user?.org?.id ? wipeTrialOrg : null;

  if (!isStaff) {
    return { menuItem: null, dialog: null, openAdminTools: () => undefined };
  }

  return {
    menuItem: (
      <DropdownMenuItem onSelect={() => setOpen(true)}>
        <GlobeIcon />
        Admin tools
      </DropdownMenuItem>
    ),
    dialog: (
      <>
        <Dialog open={open && !activeWipeOrg} onOpenChange={setOpen}>
          {!activeWipeOrg && (
            <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
              <AdminMenu
                onWipeTrialOrg={
                  canWipeTrialOrg && user?.org
                    ? () =>
                        setWipeTrialOrg({
                          id: user.org!.id,
                          name: user.org!.name,
                        })
                    : undefined
                }
              />
            </DialogContent>
          )}
        </Dialog>
        {activeWipeOrg && (
          <WipeTrialOrgDialog
            open
            onOpenChange={(nextOpen) => {
              if (!nextOpen) setWipeTrialOrg(null);
            }}
            orgId={activeWipeOrg.id}
            orgName={activeWipeOrg.name}
          />
        )}
      </>
    ),
    openAdminTools: () => setOpen(true),
  };
};

type AdminToolsTab = "permissions" | "org-flags" | "user-flags" | "impersonate";

const AdminMenu: React.FC<{ onWipeTrialOrg?: () => void }> = ({
  onWipeTrialOrg,
}) => {
  const canImpersonate = useCanImpersonate();
  const [tab, setTab] = useState<AdminToolsTab>("permissions");
  const tabs: { label: string; value: AdminToolsTab }[] = [
    { label: "Action permissions", value: "permissions" },
    { label: "Org feature flags", value: "org-flags" },
    { label: "User feature flags", value: "user-flags" },
    ...(canImpersonate
      ? [{ label: "View as someone", value: "impersonate" as const }]
      : []),
  ];
  const { userActionPermissions } = useActionPermissions();
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  const { user } = useUser();
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [updateOrgOpen, setUpdateOrgOpen] = useState(false);
  const [resetDemoOrgOpen, setResetDemoOrgOpen] = useState(false);
  const { data: orgData } = useOrgForUpdateQuery();
  const org = orgData?.Me?.user.org;
  const [initBillingOpen, setInitBillingOpen] = useState(false);
  const { addToast } = useToast();
  const [resetDemoOrg, { loading: resetLoading }] = useResetDemoOrgMutation({
    onCompleted: () => {
      launchConfetti();
      setResetDemoOrgOpen(false);
      addToast({
        message:
          "Demo org reset begun. Please check back in 1 hour to make sure everything looks right.",
      });
    },
  });
  return (
    <>
      <DialogHeader className="shrink-0 gap-3 space-y-0 border-b border-border-primary px-6 pt-4">
        <div className="flex items-start justify-between gap-4 pr-6">
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle>Admin tools</DialogTitle>
            <DialogDescription>
              {user?.org?.name} · Org ID {user?.org?.id}
            </DialogDescription>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setUpdateOrgOpen(true)}
            >
              Update org
            </Button>
            {!billingInitialized && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setInitBillingOpen(true)}
              >
                Initialize billing
              </Button>
            )}
            {user?.org?.isDemoOrg && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setResetDemoOrgOpen(true)}
              >
                Reset demo org
              </Button>
            )}
            <OptInToOctoberRelease />
            <Button size="sm" onClick={() => setCreateOrgOpen(true)}>
              Add org
            </Button>
            {onWipeTrialOrg && (
              <Button size="sm" variant="purple" onClick={onWipeTrialOrg}>
                Wipe trial org
              </Button>
            )}
          </div>
        </div>
        <SetUserType />
        <div className="-mx-2 flex">
          <Tabs
            asChild
            value={tab}
            onValueChange={(value) => setTab(value as AdminToolsTab)}
          >
            <TabsList
              aria-label="Admin tools sections"
              className="h-full min-h-0 shrink-0"
            >
              {tabs.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  data-testid={`admin-tools-tab-${t.value}`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </DialogHeader>
      <ScrollableFlexGrow flex={1} className="min-h-0">
        {tab === "permissions" && <SetActionPermissions />}
        {tab === "org-flags" && <SetOrgFeatureFlags />}
        {tab === "user-flags" && <SetEmployeeFeatureFlags />}
        {tab === "impersonate" && <ImpersonateUser />}
      </ScrollableFlexGrow>
      {initBillingOpen && (
        <InitializeBillingDialog
          open={initBillingOpen}
          onOpenChange={setInitBillingOpen}
        />
      )}
      {createOrgOpen && (
        <CreateOrgDialog open={createOrgOpen} onOpenChange={setCreateOrgOpen} />
      )}
      {updateOrgOpen && org && (
        <UpdateOrgDialog
          open={updateOrgOpen}
          onOpenChange={setUpdateOrgOpen}
          org={org}
        />
      )}
      {resetDemoOrgOpen && (
        <AlertDialog open={resetDemoOrgOpen} onOpenChange={setResetDemoOrgOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset demo org</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="flex flex-col">
              <Text variant="compact-description" multiline>
                Are you sure you want to reset your demo org? This can take up
                to two hours to complete.
              </Text>
            </div>
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={() => setResetDemoOrgOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={resetLoading}
                onClick={async () => {
                  await resetDemoOrg();
                }}
              >
                Yes, reset my demo org
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default useAdminToolsMenu;
gql`
  mutation resetDemoOrg {
    resetDemoOrg {
      success
    }
  }
`;
